// Tiendanube desde el Chat — ADR-2026-09-18-tiendanube-en-el-chat.
//
// El comerciante le habla al agente y resuelve su tienda desde ahí: traer a Atlas los
// productos que cargó en Tiendanube, y subir a la galería de un producto las fotos que
// generó acá.
//
// Molde: el mismo que los moodboards (`verMoodboard.ts`). Nada de React ni de red acá
// adentro — lo que necesita el mundo exterior se inyecta, así cada regla se prueba sola.
//
// **Por qué estas herramientas no son globales.** `V2_AGENT_TOOLS` viaja en CADA turno
// de TODOS los usuarios, y la enorme mayoría no tiene tienda conectada: sumarlas ahí
// sería cobrarle el prefijo a todo el mundo por algo que casi nadie puede usar. Tampoco
// son "de pantalla" como las del Estudio o Flujos, porque el comerciante va a pedirlo
// desde donde esté. Van por la tercera puerta, que ya existe: `herramientasExtra` se
// evalúa en cada turno, así que se mandan SÓLO si la marca activa tiene tienda — el
// mismo criterio con el que los moodboards viajan sólo para Moda.
import type { HerramientaChat } from './tipos';
import { anotarAviso, consumirAviso, puedeActuar } from './confirmacionVerificable';
import type { TnProduct } from '../lib/tiendaNube';
import type { BloqueContenido } from '../pages/v2/lotes/verProductos';

export const NOMBRE_TRAER_DE_LA_TIENDA = 'traer_productos_de_la_tienda';

/**
 * Cuántos productos se muestran CON SU FOTO al preguntar.
 *
 * Antes acá había un umbral (`TRAER_SIN_PREGUNTAR = 20`): con pocos traía derecho,
 * porque preguntar parecía una vuelta al pedo. Fue una mala decisión y se pagó el 18/09:
 * "traeme lo nuevo" trajo dos productos que la persona YA tenía cargados a mano con otro
 * nombre, y quedaron duplicados. El sistema no puede saber si algo es nuevo PARA ELLA
 * —el dedup mira el id de Tiendanube, que lo cargado a mano no tiene—, así que la única
 * que puede decidir es la persona, y para decidir necesita VER las fotos
 * (ADR-2026-09-21-traer-productos-siempre-muestra-y-pregunta).
 *
 * El tope existe porque cada foto es una imagen en el pedido al modelo: con un catálogo
 * de quinientos, mandarlas todas es caro y encima ilegible. Pasado el tope, el resto va
 * por nombre.
 */
export const FOTOS_AL_PREGUNTAR = 12;

/**
 * Cuántos productos devuelve la edge como mucho (5 páginas de 200). Si vuelve justo ese
 * número, es probable que la tienda tenga más y el resto se haya descartado en silencio:
 * hay que decirlo, no dejar que la persona crea que trajo todo.
 */
export const TOPE_DEL_CATALOGO = 1000;

/**
 * Cuántas fotos entran en un producto por llamada.
 *
 * Es el tope del endpoint (`imagenes.max(40)`), y el endpoint valida el pedido ENTERO:
 * con 41 no sube 40, no sube ninguna. Por eso se corta acá y se le avisa a la persona,
 * en vez de dejar que vuelva un error que no explica nada. El mínimo del endpoint es 1,
 * así que un producto sin ninguna foto tampoco se puede mandar.
 */
export const TOPE_FOTOS_POR_PRODUCTO = 40;

export const HERRAMIENTA_TRAER_DE_LA_TIENDA: HerramientaChat = {
  name: NOMBRE_TRAER_DE_LA_TIENDA,
  description:
    'Trae a Atlas los productos que el usuario tiene en su Tiendanube y todavía no están acá. Usala cuando pida traer, importar o sincronizar sus productos, o cuando diga que cargó algo nuevo en su tienda. Sin `nombres` trae todo lo que falte; con `nombres` sólo esos. No cuesta créditos. NUNCA trae de una: la primera llamada te devuelve lo que encontró CON LAS FOTOS de la tienda, y no trae nada. Mostráselas y preguntale cuáles quiere —puede tener alguno ya cargado en Atlas con otro nombre, y traerlo lo duplicaría—; recién con su sí volvés a llamarla con `confirmado`. Los productos tardan un rato en aparecer: decí que los estás trayendo, nunca que ya están.',
  input_schema: {
    type: 'object',
    properties: {
      nombres: {
        type: 'array',
        description:
          'Nombres de los productos a traer, como los dijo el usuario. Omitilo para traer todo lo que falte.',
        items: { type: 'string' },
      },
      confirmado: {
        type: 'boolean',
        description:
          'Poné true sólo después de haberle mostrado lo que encontré y que el usuario haya dicho que sí. Nunca en la misma respuesta en la que se lo mostrás.',
      },
    },
  },
};

export const NOMBRE_CONECTAR_TIENDA = 'conectar_tienda';

/** La pantalla donde vive el botón Conectar Tiendanube. */
export const RUTA_MI_MARCA = '/mi-marca';

/**
 * La ÚNICA que viaja cuando NO hay tienda conectada, y la contracara de las otras
 * cuatro: al que ya la tiene no se le manda, al que no la tiene se le manda sólo ésta.
 *
 * **Dibuja una tarjeta con un botón; no navega.** La primera versión llevaba al usuario
 * de una, y probándolo quedó claro por qué está mal: le cambia la pantalla mientras está
 * leyendo la respuesta. El toque lo da la persona cuando terminó de leer.
 *
 * Y el último paso, autorizar, también es suyo: el navegador sólo abre esa ventana como
 * reacción a un clic (ADR-2026-08-07-tiendanube-popup-safari), y además conectar una
 * tienda lo aprueba su dueño.
 */
export const HERRAMIENTA_CONECTAR_TIENDA: HerramientaChat = {
  name: NOMBRE_CONECTAR_TIENDA,
  description:
    'Muestra en el chat una tarjeta con el botón para ir a conectar su Tiendanube. Usala cuando diga que quiere usar Tiendanube, conectar su tienda, traer sus productos o subir fotos allá, y vos NO tengas las herramientas de Tiendanube (eso significa que no la tiene conectada). No lo lleva a ningún lado: el botón lo toca él. Contale en una línea qué va a pasar y mostrá la tarjeta en la misma respuesta; no hace falta pedirle permiso para mostrarla. Después tiene que tocar Conectar y autorizar en la ventana de Tiendanube. Cuando termine y te escriba, ya vas a tener las otras herramientas.',
  input_schema: { type: 'object', properties: {} },
};

// ── Estado de la tienda, para el bloque de contexto ─────────────────────────

export interface EstadoDeLaTienda {
  conectada: boolean;
  nombre: string | null;
  /** `true` = no se pudo averiguar. NO es lo mismo que "no conectada". */
  error: boolean;
}

/**
 * La línea que viaja en el contexto de cada turno.
 *
 * Sólo dice si hay tienda y cómo se llama: eso sale de nuestra base y **no toca la API
 * de Tiendanube**. Cuántos productos faltan traer sí la toca, así que eso se averigua
 * cuando la persona pregunta y no antes — es lo que le prometimos a Tiendanube en la
 * homologación: ninguna consulta a su API que no nazca de una acción del comerciante.
 */
export function bloqueDeLaTienda(estado: EstadoDeLaTienda): string | null {
  if (estado.error || !estado.conectada) return null;
  const tienda = estado.nombre ? `"${estado.nombre}"` : 'su tienda';
  return `══════════ TIENDANUBE ══════════\nEl usuario tiene ${tienda} conectada a Atlas. Podés traer a Atlas los productos que cargó allá y todavía no están acá con \`${NOMBRE_TRAER_DE_LA_TIENDA}\` (no cuesta créditos). No sabés cuántos faltan hasta que la llamás. No ofrezcas esto en cada respuesta: sólo cuando venga al caso.`;
}

// ── Elegir qué productos traer ──────────────────────────────────────────────

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Lo que el modelo escribió en `nombres`, limpio. Vacío = todos los pendientes. */
export function nombresPedidos(input: unknown): string[] {
  const crudos = (input as { nombres?: unknown } | null)?.nombres;
  if (!Array.isArray(crudos)) return [];
  return crudos.filter((n): n is string => typeof n === 'string' && n.trim().length > 0);
}

export interface EleccionDeProductos {
  elegidos: TnProduct[];
  /** Los nombres que no encontraron ninguno. */
  sinMatch: string[];
  /** Los nombres que encontraron varios: no se elige por la persona. */
  ambiguos: { nombre: string; opciones: string[] }[];
}

/**
 * Cruza los nombres que dijo el usuario con los productos pendientes.
 *
 * Primero busca el nombre exacto; si no hay, el que lo contenga. **Con más de uno no
 * elige**: devolver las opciones y no hacer nada es mejor que traer el equivocado, que
 * después hay que borrar a mano.
 */
export function elegirProductos(pedidos: string[], pendientes: TnProduct[]): EleccionDeProductos {
  if (pedidos.length === 0) return { elegidos: pendientes, sinMatch: [], ambiguos: [] };
  const elegidos: TnProduct[] = [];
  const sinMatch: string[] = [];
  const ambiguos: { nombre: string; opciones: string[] }[] = [];
  for (const pedido of pedidos) {
    const buscado = normalizar(pedido);
    const exactos = pendientes.filter((p) => normalizar(p.nombre) === buscado);
    const candidatos =
      exactos.length > 0 ? exactos : pendientes.filter((p) => normalizar(p.nombre).includes(buscado));
    if (candidatos.length === 0) sinMatch.push(pedido);
    else if (candidatos.length > 1)
      ambiguos.push({ nombre: pedido, opciones: candidatos.map((c) => c.nombre) });
    else if (!elegidos.some((e) => e.id === candidatos[0].id)) elegidos.push(candidatos[0]);
  }
  return { elegidos, sinMatch, ambiguos };
}

// ── El resolvedor ───────────────────────────────────────────────────────────

export interface DependenciasDeTraer {
  /** Los productos de la tienda que faltan traer. Lee la API de Tiendanube. */
  pendientes: () => Promise<{ conectada: boolean; nombre: string | null; productos: TnProduct[] }>;
  /** Los manda a importar. Devuelve cuántos salieron DE VERDAD. */
  importar: (productos: TnProduct[]) => Promise<{ enviados: number }>;
}

const lista = (nombres: string[], tope = 12): string => {
  const visibles = nombres.slice(0, tope);
  const resto = nombres.length - visibles.length;
  return visibles.join(', ') + (resto > 0 ? ` y ${resto} más` : '');
};

const productos = (n: number) => (n === 1 ? '1 producto' : `${n} productos`);

/**
 * Lo que se le muestra antes de traer: qué encontró, CON las fotos de la tienda.
 *
 * Las fotos son la mitad del asunto. Con los nombres solos, "cartera" y "Nuevo producto
 * 65" parecen cosas distintas aunque sean la misma; con las dos fotos al lado, la
 * persona lo ve en un segundo. Las urls son las de la tienda, que son públicas: no hay
 * nada que firmar.
 */
export function bloquesParaConfirmarTraer(args: {
  tienda: string;
  elegidos: TnProduct[];
  aviso: string;
  noEncontrados: string;
}): BloqueContenido[] {
  const { tienda, elegidos, aviso, noEncontrados } = args;
  const conFoto = elegidos.slice(0, FOTOS_AL_PREGUNTAR);
  const resto = elegidos.slice(conFoto.length);
  const bloques: BloqueContenido[] = [
    {
      type: 'text',
      text:
        `NO TRAJE NADA TODAVÍA. Faltan traer ${productos(elegidos.length)} de ${tienda}. ` +
        `Mostráselos y preguntale cuáles quiere: puede que alguno ya lo tenga cargado en Atlas con otro nombre, ` +
        `y traerlo lo dejaría dos veces. Si dice que sí a todos, volvé a llamarme con confirmado en true; ` +
        `si quiere sólo algunos, pasame esos nombres.${noEncontrados}${aviso}`,
    },
  ];
  conFoto.forEach((p, i) => {
    bloques.push({ type: 'text', text: `#${i + 1} ${p.nombre}` });
    const foto = p.img_src || p.imgs?.[0];
    if (foto) bloques.push({ type: 'image', source: { type: 'url', url: foto } });
  });
  if (resto.length > 0) {
    bloques.push({
      type: 'text',
      text: `Y ${productos(resto.length)} más, sin foto para no hacerlo eterno: ${lista(resto.map((p) => p.nombre))}.`,
    });
  }
  return bloques;
}

/**
 * Trae los productos pedidos, o muestra lo que encontró y espera.
 *
 * **Nunca trae sin preguntar**, ni siquiera uno: ver la foto es lo único que deja
 * distinguir un producto nuevo de uno que la persona ya tiene cargado a mano
 * (ADR-2026-09-21-traer-productos-siempre-muestra-y-pregunta).
 *
 * Nunca dice "listo": la importación se encola y el worker los va creando, así que lo
 * honesto es "los estoy trayendo". Y cuenta los que SALIERON, no los que se pidieron:
 * las tandas se mandan una atrás de otra desde el navegador y pueden cortarse.
 */
export async function resolverTraerDeLaTienda(
  input: unknown,
  deps: DependenciasDeTraer,
  ctx: { turno: number | null; ultimoMensaje?: string } = { turno: null },
): Promise<{ __texto: string } | { __bloques: BloqueContenido[] }> {
  const texto = (t: string) => ({ __texto: t });

  let estado: Awaited<ReturnType<DependenciasDeTraer['pendientes']>>;
  try {
    estado = await deps.pendientes();
  } catch {
    return texto(
      'No se pudo consultar la tienda en este momento. Decíselo así, sin inventar una causa, y ofrecé volver a intentar en un rato. No repitas la herramienta ahora.',
    );
  }

  if (!estado.conectada) {
    return texto(
      'La tienda no está conectada. Pedile que la conecte desde Mi marca y NO vuelvas a llamar esta herramienta hasta que te diga que ya lo hizo. No podés conectarla vos.',
    );
  }

  const tienda = estado.nombre ? `"${estado.nombre}"` : 'la tienda';
  if (estado.productos.length === 0) {
    return texto(
      `No hay productos nuevos: todo lo que está en ${tienda} ya está en Atlas. Decíselo en una línea.`,
    );
  }

  const aviso =
    estado.productos.length >= TOPE_DEL_CATALOGO
      ? ` Ojo: la tienda tiene muchos productos y sólo se pueden leer los primeros ${TOPE_DEL_CATALOGO}; avisale que puede quedar algo afuera.`
      : '';

  const pedidos = nombresPedidos(input);
  const { elegidos, sinMatch, ambiguos } = elegirProductos(pedidos, estado.productos);

  if (ambiguos.length > 0) {
    const detalle = ambiguos
      .map((a) => `por "${a.nombre}" hay varios: ${lista(a.opciones)}`)
      .join('; ');
    return texto(
      `No traje nada todavía porque ${detalle}. Preguntale cuál quiere y volvé a llamarme con el nombre exacto.`,
    );
  }

  if (elegidos.length === 0) {
    return texto(
      `No encontré ${sinMatch.length === 1 ? `"${sinMatch[0]}"` : `ninguno de: ${lista(sinMatch)}`} entre los que faltan traer. Los pendientes son: ${lista(estado.productos.map((p) => p.nombre))}. Mostraselos y preguntá cuál.`,
    );
  }

  const noEncontrados =
    sinMatch.length > 0 ? ` No encontré ${lista(sinMatch)}: avisáselo también.` : '';

  // El mismo candado que lo irreversible, y por un motivo parecido: traer duplica
  // productos en Atlas y eso se deshace de a uno. Que el modelo mande `confirmado` no
  // alcanzaba — podía recibir la lista con las fotos y volver a llamar en el MISMO turno,
  // antes de que la persona dijera nada, que es exactamente lo que esto venía a evitar
  // (review Greptile #127). Ahora tiene que haber un mensaje suyo posterior, y tiene que
  // ser un sí.
  const confirmado = (input as { confirmado?: unknown } | null)?.confirmado === true;
  const clave = elegidos
    .map((p) => p.id)
    .sort((a, b) => a - b)
    .join(',');
  if (
    !puedeActuar({
      accion: NOMBRE_TRAER_DE_LA_TIENDA,
      clave,
      confirmado,
      turno: ctx.turno,
      ultimoMensaje: ctx.ultimoMensaje,
    })
  ) {
    anotarAviso(NOMBRE_TRAER_DE_LA_TIENDA, clave, ctx.turno);
    return { __bloques: bloquesParaConfirmarTraer({ tienda, elegidos, aviso, noEncontrados }) };
  }

  // El permiso se usa UNA vez: sin esto, otro "dale" más adelante repetía la importación
  // sin volver a mostrar nada, y duplicaba productos antes de que el catálogo reflejara
  // la primera (review Greptile #127, segunda pasada).
  consumirAviso(NOMBRE_TRAER_DE_LA_TIENDA);
  const { enviados } = await deps.importar(elegidos);
  if (enviados === 0) {
    return texto(
      'No se pudo mandar ninguno a traer. Decíselo sin inventar la causa y ofrecé reintentar.',
    );
  }
  const incompleto =
    enviados < elegidos.length
      ? ` De ${productos(elegidos.length)} salieron ${enviados}: contale que el resto quedó sin mandar y que vuelva a pedirlo.`
      : '';
  return texto(
    `Mandé a traer ${productos(enviados)} de ${tienda}: ${lista(elegidos.slice(0, enviados).map((p) => p.nombre))}. Tardan un rato en aparecer en Atlas, así que decí que los estás trayendo y que van a ir apareciendo — NUNCA que ya están.${incompleto}${noEncontrados}${aviso}`,
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Subir fotos al producto en la tienda — fase 2
// ════════════════════════════════════════════════════════════════════════════

export const NOMBRE_SUBIR_FOTOS = 'subir_fotos_a_la_tienda';

export const HERRAMIENTA_SUBIR_FOTOS: HerramientaChat = {
  name: NOMBRE_SUBIR_FOTOS,
  description:
    'Sube fotos que ya generaste a la galería de un producto en la Tiendanube del usuario. AGREGA: no toca ni borra las fotos que el producto ya tiene. Usala cuando diga "subila a mi tienda", "mandala al producto", "publicá esta foto". `imagenes` son los image_id de las fotos (los mismos que usás como referencia con source "generada"). `producto` es el producto de Atlas, por nombre o por id. Si el producto nunca vino de Tiendanube no sé cuál es allá: te voy a dar los candidatos para que le preguntes, y después me la volvés a llamar con `producto_en_la_tienda`. No cuesta créditos. Tarda un rato: decí que la estás subiendo, nunca que ya está.',
  input_schema: {
    type: 'object',
    properties: {
      producto: {
        type: 'string',
        description: 'El producto de Atlas: su nombre tal como lo dijo el usuario, o su id.',
      },
      imagenes: {
        type: 'array',
        description: 'Los image_id de las fotos a subir, en el orden en que van a la galería.',
        items: { type: 'string' },
      },
      producto_en_la_tienda: {
        type: 'number',
        description:
          'El id del producto EN LA TIENDA. Mandalo sólo cuando te lo pedí y el usuario confirmó cuál era.',
      },
    },
    required: ['producto', 'imagenes'],
  },
};

/**
 * Una variante del producto: es lo que el chat llama "producto".
 *
 * El catálogo del agente, `pick_product` y los productos que la persona adjunta salen
 * todos de la tabla `variantes` (`productsApi.fetchUserProducts`), así que el id que el
 * modelo tiene a mano es el de la VARIANTE y el nombre que ve es el de la variante.
 * Tiendanube, en cambio, trabaja a nivel PRODUCTO: el vínculo con la tienda
 * (`tn_product_id`) y la galería de fotos son del producto, no de cada talle.
 * Por eso el producto viaja con sus variantes: es lo que deja cruzar los dos mundos
 * (ADR-2026-09-21-el-chat-habla-de-variantes-y-la-tienda-de-productos).
 */
export interface VarianteDeAtlas {
  id: string;
  nombre: string;
}

/** Un producto del catálogo de Atlas, con el vínculo a la tienda si lo tiene. */
export interface ProductoDeAtlas {
  id: string;
  nombre: string;
  /** `productos.tn_product_id`: de qué producto de la tienda vino. `null` = cargado a mano. */
  tnProductId: number | null;
  /**
   * Las variantes activas del producto, con las que el chat lo nombra. Opcional a
   * propósito: sin ellas todo sigue funcionando como antes —se busca sólo por el id y el
   * nombre del producto—, que es lo que hacen los tests que no las necesitan.
   */
  variantes?: VarianteDeAtlas[];
}

/** Una imagen generada, con el rastro de si ya viajó a la tienda. */
export interface ImagenGenerada {
  id: string;
  fileName: string;
  /** A qué producto de la tienda fue, si fue. */
  tnProductId: number | null;
}

export type EleccionDeProducto =
  | { tipo: 'uno'; producto: ProductoDeAtlas }
  | { tipo: 'varios'; opciones: ProductoDeAtlas[] }
  | { tipo: 'ninguno' };

/** Todos los nombres con los que se puede nombrar a un producto: el suyo y los de sus variantes. */
const nombresDe = (p: ProductoDeAtlas): string[] => [p.nombre, ...(p.variantes ?? []).map((v) => v.nombre)];

/**
 * Cruza lo que dijo el usuario con el catálogo de Atlas.
 *
 * Acepta el id directo, y ahí está el cruce que importa: lo que el modelo tiene a mano
 * —lo que devuelve `pick_product`, lo que la persona adjunta— es el id de una VARIANTE,
 * no el del producto. Buscar sólo por `productos.id` no acertaba nunca por ese camino:
 * caía al nombre, y con tres productos que se llaman igual terminaba en "hay varios" o
 * en "no lo encontré" (visto el 21/09 con tres "Remera negra" de la misma marca).
 *
 * Si no es un id, busca por nombre —el del producto o el de cualquiera de sus
 * variantes—: exacto primero, contenido después, y con varios no elige.
 */
export function elegirProductoDeAtlas(
  pedido: string,
  productos: ProductoDeAtlas[],
): EleccionDeProducto {
  const porId = productos.find(
    (p) => p.id === pedido || (p.variantes ?? []).some((v) => v.id === pedido),
  );
  if (porId) return { tipo: 'uno', producto: porId };
  const buscado = normalizar(pedido);
  if (!buscado) return { tipo: 'ninguno' };
  const exactos = productos.filter((p) => nombresDe(p).some((n) => normalizar(n) === buscado));
  const candidatos =
    exactos.length > 0
      ? exactos
      : productos.filter((p) => nombresDe(p).some((n) => normalizar(n).includes(buscado)));
  if (candidatos.length === 0) return { tipo: 'ninguno' };
  if (candidatos.length > 1) return { tipo: 'varios', opciones: candidatos };
  return { tipo: 'uno', producto: candidatos[0] };
}

/**
 * Con qué id se lo nombra a `pick_product`: el de la primera variante, porque el
 * catálogo que esa tarjeta dibuja es el de variantes. Sin variantes cargadas queda el
 * del producto, que al menos no es un id inventado.
 */
export const idParaElegir = (p: ProductoDeAtlas): string => p.variantes?.[0]?.id ?? p.id;

/**
 * Qué contestarle al modelo cuando hay varios que se llaman parecido.
 *
 * Listar los nombres no alcanza —los tres decían "Remera negra"—: con los nombres
 * iguales, la persona no puede elegir por texto. Por eso se le pide la tarjeta de
 * siempre, que muestra las FOTOS, y se le pasan los ids que esa tarjeta entiende.
 */
export function mensajeDeVarios(pedido: string, opciones: ProductoDeAtlas[]): string {
  const ids = opciones.map(idParaElegir);
  return (
    `No hice nada: por "${pedido}" hay ${opciones.length} productos en el catálogo de Atlas ` +
    `(${lista(opciones.map((o) => o.nombre))}), y por el nombre no se distinguen. ` +
    `Mostrale pick_product con candidate_ids ${JSON.stringify(ids)} para que elija por la foto, ` +
    `y volvé a llamarme con el id que haya elegido.`
  );
}

/**
 * Qué imágenes hay que subir de verdad.
 *
 * Una foto que YA fue a ese mismo producto no se vuelve a subir: quedaría dos veces en
 * la galería y hay que borrarla desde Tiendanube. La misma foto en OTRO producto sí se
 * sube: es un pedido distinto.
 *
 * Ojo con el alcance de esta red: la marca la escribe el worker DESPUÉS de subir, así
 * que no protege contra dos llamadas seguidas en el mismo turno. Para eso está el
 * registro en memoria de `recordarSubida`.
 */
export function imagenesQueFaltanSubir(
  imagenes: ImagenGenerada[],
  tnProductId: number,
): { nuevas: ImagenGenerada[]; repetidas: ImagenGenerada[] } {
  const nuevas: ImagenGenerada[] = [];
  const repetidas: ImagenGenerada[] = [];
  for (const i of imagenes) (i.tnProductId === tnProductId ? repetidas : nuevas).push(i);
  return { nuevas, repetidas };
}

/**
 * Lo que ya se mandó a subir en esta pestaña, para que una segunda llamada seguida no
 * duplique la foto.
 *
 * Hace falta porque subir es fire-and-forget: el worker marca la imagen recién cuando
 * Tiendanube le contesta, así que entre dos llamadas del modelo la base todavía no sabe
 * nada. Vive en memoria del módulo a propósito: es una red contra el reintento de un
 * turno, no un registro de lo que pasó (eso es `imagenes_generadas.tn_product_id`).
 */
const subidasRecientes = new Map<string, number>();

const claveDeSubida = (imagenId: string, tnProductId: number) => `${imagenId}→${tnProductId}`;

export function recordarSubida(imagenId: string, tnProductId: number): void {
  subidasRecientes.set(claveDeSubida(imagenId, tnProductId), Date.now());
}

export function yaSeSubioReciente(imagenId: string, tnProductId: number): boolean {
  return subidasRecientes.has(claveDeSubida(imagenId, tnProductId));
}

/** Para los tests: cada caso arranca sin memoria. */
export function olvidarSubidas(): void {
  subidasRecientes.clear();
}

export interface DependenciasDeSubir {
  /**
   * Los productos de Atlas de la marca, con su vínculo a la tienda.
   *
   * Es una consulta y no la lista que el chat ya tiene a mano porque esa está a nivel
   * VARIANTE (cada color es una entrada) y acá hace falta el producto, que es la unidad
   * con la que trabaja la galería de Tiendanube.
   */
  productosDeAtlas: () => Promise<ProductoDeAtlas[]>;
  /** Resuelve las imágenes pedidas. Las que no existen no vuelven. */
  imagenes: (ids: string[]) => Promise<ImagenGenerada[]>;
  /** El catálogo de la tienda. Se llama SÓLO si hay que averiguar el vínculo. */
  catalogoDeLaTienda: () => Promise<TnProduct[]>;
  /**
   * Manda las fotos. Devuelve CUÁLES salieron (`imagenIds`) y, si no salió ninguna, por
   * qué. Los ids importan: una foto que no se pudo firmar se descarta en el armado del
   * pedido, y marcarla como subida haría que el reintento conteste "esa ya está" por algo
   * que nunca viajó (review Greptile #127).
   */
  subir: (args: {
    productoId: string;
    tnProductId: number;
    imagenes: ImagenGenerada[];
  }) => Promise<{ imagenes: number; imagenIds?: string[]; motivo?: string }>;
  /** Deja guardado qué producto de la tienda es, para no volver a preguntar. */
  guardarVinculo: (productoId: string, tnProductId: number) => Promise<void>;
}

const fotos = (n: number) => (n === 1 ? '1 foto' : `${n} fotos`);

/**
 * Sube las fotos pedidas a la galería del producto en la tienda.
 *
 * El caso que más aparece no es el feliz: la mayoría de los productos de Atlas se
 * cargaron a mano y **no saben cuál son en la tienda**. Ahí esto no adivina: busca por
 * nombre, propone y espera que el usuario confirme. Cuando confirma, el vínculo queda
 * guardado y no se vuelve a preguntar nunca más para ese producto.
 */
export async function resolverSubirFotos(
  input: unknown,
  deps: DependenciasDeSubir,
): Promise<{ __texto: string }> {
  const texto = (t: string) => ({ __texto: t });
  const pedido = (input ?? {}) as {
    producto?: unknown;
    imagenes?: unknown;
    producto_en_la_tienda?: unknown;
  };

  const nombreProducto = typeof pedido.producto === 'string' ? pedido.producto.trim() : '';
  if (!nombreProducto) return texto('Falta decir a qué producto. Preguntale cuál.');

  const idsPedidos = Array.isArray(pedido.imagenes)
    ? pedido.imagenes.filter((i): i is string => typeof i === 'string' && i.trim().length > 0)
    : [];
  if (idsPedidos.length === 0) {
    return texto('Falta decir qué fotos subir. Preguntale cuáles, o mostrale las últimas.');
  }

  // 1. De qué producto de Atlas hablamos.
  let catalogoDeAtlas: ProductoDeAtlas[];
  try {
    catalogoDeAtlas = await deps.productosDeAtlas();
  } catch {
    return texto('No se pudo leer tu catálogo de Atlas. Decíselo y ofrecé reintentar.');
  }
  const eleccion = elegirProductoDeAtlas(nombreProducto, catalogoDeAtlas);
  if (eleccion.tipo === 'varios') {
    return texto(
      mensajeDeVarios(nombreProducto, eleccion.opciones),
    );
  }
  if (eleccion.tipo === 'ninguno') {
    return texto(
      `No encontré "${nombreProducto}" entre los productos de Atlas. Preguntale a cuál se refiere; si es uno de su tienda que todavía no trajo, ofrecé traerlo primero con ${NOMBRE_TRAER_DE_LA_TIENDA}.`,
    );
  }
  const producto = eleccion.producto;

  // 2. Cuál es ese producto EN LA TIENDA.
  const confirmado =
    typeof pedido.producto_en_la_tienda === 'number' ? pedido.producto_en_la_tienda : null;
  let tnProductId = producto.tnProductId ?? confirmado;
  if (tnProductId === null) {
    // El producto se cargó a mano en Atlas: no sabemos cuál es allá. No se adivina.
    let catalogo: TnProduct[];
    try {
      catalogo = await deps.catalogoDeLaTienda();
    } catch {
      return texto(
        'No se pudo consultar la tienda para saber a qué producto corresponde. Decíselo y ofrecé reintentar.',
      );
    }
    const buscado = normalizar(producto.nombre);
    const exactos = catalogo.filter((c) => normalizar(c.nombre) === buscado);
    const candidatos =
      exactos.length > 0 ? exactos : catalogo.filter((c) => normalizar(c.nombre).includes(buscado));
    if (candidatos.length === 0) {
      return texto(
        `"${producto.nombre}" está en Atlas pero no lo encuentro en la tienda, así que no sé a dónde subir la foto. Preguntale cómo se llama allá, o si todavía no lo creó.`,
      );
    }
    if (candidatos.length > 1) {
      const opciones = candidatos.map((c) => `"${c.nombre}" (id ${c.id})`);
      return texto(
        `No subí nada: en la tienda hay varios que se parecen a "${producto.nombre}" — ${lista(opciones)}. Preguntale cuál es y volvé a llamarme pasando ese id en producto_en_la_tienda.`,
      );
    }
    const unico = candidatos[0];
    return texto(
      `"${producto.nombre}" no vino de Tiendanube, así que no tengo guardado cuál es allá. El que más se parece es "${unico.nombre}" (id ${unico.id}). Preguntale si es ese y, si dice que sí, volvé a llamarme con producto_en_la_tienda: ${unico.id}. No subí nada todavía.`,
    );
  }

  // 3. Qué fotos faltan subir de verdad.
  const imagenes = await deps.imagenes(idsPedidos);
  if (imagenes.length === 0) {
    return texto('No encontré esas fotos. Mostrale las últimas que generó y preguntá cuál quiere.');
  }
  // Las que el modelo pidió y no existen: se avisan, pero no frenan a las demás.
  const perdidas = idsPedidos.length - imagenes.length;
  const { nuevas, repetidas } = imagenesQueFaltanSubir(imagenes, tnProductId);
  const sinRepetirEnEsteTurno = nuevas.filter((i) => !yaSeSubioReciente(i.id, tnProductId!));
  const yaEnCurso = nuevas.length - sinRepetirEnEsteTurno.length;

  if (sinRepetirEnEsteTurno.length === 0) {
    const cuantas = repetidas.length + yaEnCurso;
    return texto(
      `No subí nada: ${cuantas === 1 ? 'esa foto ya está' : `esas ${cuantas} fotos ya están`} en "${producto.nombre}" de la tienda. Decíselo; si quiere otra copia, tiene que hacerlo desde Tiendanube.`,
    );
  }

  // 4. Subir. El orden es el que pidió la persona: la primera de la lista queda primera
  // de las nuevas en la galería. Y se corta en el tope, porque el endpoint valida el
  // pedido entero y con una de más no subiría NINGUNA.
  const vanAhora = sinRepetirEnEsteTurno.slice(0, TOPE_FOTOS_POR_PRODUCTO);
  const quedanParaDespues = sinRepetirEnEsteTurno.length - vanAhora.length;
  const { imagenes: subidas, imagenIds, motivo } = await deps.subir({
    productoId: producto.id,
    tnProductId,
    imagenes: vanAhora,
  });
  if (subidas === 0) {
    // El backend valida antes de encolar: si la tienda se desconectó mientras tanto, lo
    // dice con ese código. Es lo más común, y decirlo bien evita que la persona espere
    // una foto que nunca va a llegar.
    if (motivo === 'tienda_no_conectada') {
      return texto(
        'No subí nada: la tienda dejó de estar conectada. Decíselo y pedile que la vuelva a conectar desde Mi marca.',
      );
    }
    return texto('No se pudo mandar ninguna foto. Decíselo sin inventar la causa y ofrecé reintentar.');
  }
  // Sólo las que viajaron. Sin `imagenIds` (un llamador viejo) se cae a las que se
  // pidieron, que es lo que había antes.
  const viajaron = new Set(imagenIds ?? vanAhora.map((i) => i.id));
  for (const i of vanAhora) if (viajaron.has(i.id)) recordarSubida(i.id, tnProductId);
  const descartadas = vanAhora.filter((i) => !viajaron.has(i.id)).length;

  // 5. Si el vínculo lo acaba de confirmar el usuario, guardarlo para no preguntar más.
  if (producto.tnProductId === null && confirmado !== null) {
    try {
      await deps.guardarVinculo(producto.id, confirmado);
    } catch {
      /* que no se guarde el vínculo no invalida la subida: sólo vuelve a preguntar */
    }
  }

  const repetidasAviso =
    repetidas.length > 0
      ? ` (${fotos(repetidas.length)} ya estaban ahí y no las repetí)`
      : '';
  const descartadasAviso =
    descartadas > 0
      ? ` ${descartadas === 1 ? 'Una foto no se pudo preparar y no fue' : `${descartadas} fotos no se pudieron preparar y no fueron`}: avisale que puede volver a pedirlas.`
      : '';
  const perdidasAviso =
    perdidas > 0 ? ` ${perdidas === 1 ? 'Una de las que pasaste no existe' : `${perdidas} de las que pasaste no existen`} y no fue: avisáselo.` : '';
  const restoAviso =
    quedanParaDespues > 0
      ? ` Entran hasta ${TOPE_FOTOS_POR_PRODUCTO} por vez, así que ${fotos(quedanParaDespues)} quedaron afuera: contáselo y ofrecé mandarlas en otra tanda.`
      : '';
  return texto(
    `Mandé ${fotos(subidas)} a la galería de "${producto.nombre}" en la tienda${repetidasAviso}, en el orden en que me las pasaste. Tarda un rato en aparecer allá: decí que la estás subiendo, NUNCA que ya está. Se agregan al final de la galería, sin tocar las fotos que el producto ya tenía.${restoAviso}${descartadasAviso}${perdidasAviso}`,
  );
}
